import { useState, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, User, Mail, Lock, CheckCircle, XCircle, Loader2, ChevronRight } from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';

/**
 * Kullanıcı kayıt formunu içeren React bileşeni.
 * Standart kayıt, Google ile kayıt ve misafir olarak devam et seçeneklerini sunar.
 */
function RegisterForm({ onRegister }) {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // Standart kayıt işlemi
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isChecked) {
      setError("Kayıt olmak için kullanım koşullarını kabul etmelisin.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Şifreler uyuşmuyor.");
      return;
    }

    setLoading(true);
    setError(null);
    setAwaitingVerification(false);

    try {
      const res = await fetch(`${API_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Kayıt başarısız.");
      }

      setAwaitingVerification(true);
      setError(null);
    } catch (err) {
      setError(err.message);
      setAwaitingVerification(false);
    } finally {
      setLoading(false);
    }
  };

  // Google ile kayıt/giriş işlemi
  const handleLoginSuccess = async (codeResponse) => {
    if (!isChecked) {
      setError("Kayıt olmak için kullanım koşullarını kabul etmelisin.");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/auth/google/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: codeResponse.code }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.detail || "Giriş başarısız.");

      localStorage.setItem("token", data.access_token);
      localStorage.setItem('isNewUser', 'true');

      onRegister?.(codeResponse);
      navigate("/profile");
  
    } catch (error) {
      console.error("Sunucu ile iletişimde hata:", error.message);
      setError("Google ile giriş yapılırken bir hata oluştu: " + error.message);
    }
  };

  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: handleLoginSuccess,
    onError: (err) => {
      console.error("Google Login Hatası:", err);
      setError("Google ile oturum açma işlemi başarısız.");
    }
  });

  // Misafir olarak giriş (API çağrısı yapmadan, yerel depolama ile simüle)
const handleGuestLogin = async () => {
  try {
    setLoading(true);
    setError(null);

    const res = await fetch(`${API_URL}/auth/guest-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || "Misafir girişi başarısız.");
    }

    localStorage.setItem("token", data.access_token);
    localStorage.setItem('isNewUser', 'true');

    if (data.is_guest !== undefined) {
      localStorage.setItem("is_guest", data.is_guest);
    }

    onRegister()
    navigate("/profile");

  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 font-sans text-gray-50">
      <div className="w-full max-w-md bg-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-700">
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <UserPlus className="h-10 w-10 text-amber-400" />
            <h2 className="text-3xl font-bold text-gray-50">Kayıt Ol</h2>
          </div>
          <p className="text-gray-400 mt-2">
            Hemen bir hesap oluşturarak Mekânname'nin tüm özelliklerini keşfet.
          </p>
        </header>

        {error && !awaitingVerification && (
          <div className="flex items-center gap-3 p-4 mb-6 text-red-400 bg-red-900/30 rounded-xl border border-red-700">
            <XCircle className="h-6 w-6 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {awaitingVerification && (
          <div className="flex flex-col items-center gap-3 p-6 mb-6 text-green-400 bg-green-900/30 rounded-xl border border-green-700 text-center">
            <CheckCircle className="h-8 w-8 text-green-400 animate-bounce" />
            <p className="font-semibold text-lg">Kayıt başarılı!</p>
            <p className="text-gray-300 mt-2">
              Hesabını doğrulamak için e-posta adresine bir bağlantı gönderdik. Lütfen gelen kutunu kontrol et.
            </p>
          </div>
        )}

        {!awaitingVerification && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium text-gray-300">Kullanıcı Adı</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  placeholder="Bir kullanıcı adı belirle..."
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-gray-300">E-posta Adresi</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  placeholder="E-posta adresinizi girin..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-300">Şifre</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="password"
                  placeholder="Şifreni gir..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">Şifre Tekrar</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="confirmPassword"
                  placeholder="Şifreni tekrar gir..."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="terms-agreement"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                className="h-5 w-5 rounded-md text-amber-400 bg-gray-700 border-gray-600 focus:ring-amber-500"
              />
              <label htmlFor="terms-agreement" className="text-sm font-medium text-gray-300">
                <Link to="/service" className="text-indigo-400 hover:underline">Kullanım Koşullarını</Link> Okudum ve Kabul Ediyorum
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !isChecked}
              className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors 
                ${loading || !isChecked ? 'bg-indigo-800/50 text-indigo-200/50 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Kayıt Olunuyor...
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5" /> Kayıt Ol
                </>
              )}
            </button>
          </form>
        )}

        {!awaitingVerification && (
          <div className="mt-8 text-center">
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-gray-800 px-3 text-gray-500">VEYA</span>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="button"
                onClick={() => login()}
                disabled={!isChecked}
                className={`w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-600 bg-gray-700 text-white font-semibold rounded-lg shadow-sm transition-colors
                  ${!isChecked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'}`}
              >
                <img
                  src="https://www.google.com/favicon.ico"
                  alt="Google icon"
                  className="h-5 w-5"
                />
                Google ile Kayıt Ol
              </button>

              <button
                type="button"
                onClick={handleGuestLogin}
                disabled={!isChecked}
                className={`w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-600 bg-gray-700 text-white font-semibold rounded-lg shadow-sm transition-colors
                  ${!isChecked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'}`}
              >
                <ChevronRight className="h-5 w-5" />
                Misafir Olarak Devam Et
              </button>
            </div>

            <p className="mt-6 text-gray-400">
              Zaten bir hesabın var mı?{" "}
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-semibold transition-colors">Giriş Yap</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(RegisterForm);
